import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Button from "common/components/Button/Button";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";
import useAuth from "common/hooks/useAuth";
import Icon from "components/Icon/Icon";
import { SavedBalanceSheet } from "types/SavedBalanceSheet";

function SavedBalanceSheetItem({ item, onDelete, onNavigate }: any) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  const {
    id,
    name,
    startDate,
    endDate,
    isFrozen,
    authorUsername,
    authorId,
    createdAt,
  } = item as SavedBalanceSheet;

  // A saved balance sheet is shared with the whole park, but only its author and
  // the administrators may remove it.
  const canDeleteItem =
    (currentUser?.isSuperadmin ||
      currentUser?.isAdmin ||
      authorId === currentUser?.id) ??
    false;

  const detailLink = `/saved-balance-sheets/${id}`;

  return (
    <div className="flex px-4 py-4 mt-2 w-full relative bg-white cursor-pointer rounded-lg border border-slate-150 transition-all">
      <Link to={detailLink} className="flex flex-1">
        <div className="flex flex-col flex-1 justify-between pl-4 pr-6 gap-2">
          <h4 className="text-xl font-bold text-primary leading-5">{name}</h4>
          <div className="flex flex-row items-center gap-2 text-sm">
            <Icon
              name="CalendarDaysIcon"
              className="w-4 h-4 text-emerald-700"
            />
            <span>
              {dayjs(startDate).format("L")}
              {" → "}
              {dayjs(endDate).format("L")}
            </span>
          </div>
          <span
            className={`self-start inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${
              isFrozen
                ? "bg-emerald-100 text-emerald-800"
                : "bg-orange-100 text-orange-800"
            }`}
          >
            {isFrozen ? t("balance.frozenData") : t("balance.liveData")}
          </span>
        </div>
      </Link>

      <div className="flex flex-col justify-between items-end text-xs lg:border-l pl-4 lg:min-w-[22em] border-slate-150 gap-2">
        <div className="flex flex-col items-end gap-1 text-sm">
          {authorUsername && (
            <span className="font-bold">{authorUsername}</span>
          )}
          <span>
            {t("balance.savedOn", { date: dayjs(createdAt).format("LLL") })}
          </span>
        </div>
        <div className="flex w-full items-center justify-end text-slate-500 gap-1">
          <TooltipWrapper content={<span>{t("actions.delete")}</span>}>
            <Button
              type="error-line"
              icon="TrashIcon"
              compact
              onClick={() => onDelete(id)}
              disabled={!canDeleteItem}
              confirmMessage={t("actions.confirmDelete")}
            />
          </TooltipWrapper>

          <Button
            title={t("actions.see")}
            type="primary-line"
            icon="EyeIcon"
            onClick={() => onNavigate(id)}
          />
        </div>
      </div>
    </div>
  );
}

export default SavedBalanceSheetItem;
